import { useState, type ReactNode } from 'react'
import { AlertTriangle, Info, X, XOctagon } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { toneSoftClass, type V2Tone } from './tone'
import { readUrgentMessageDismissed, writeUrgentMessageDismissed } from './urgentMessageSession'

export type UrgentLevel = 'info' | 'warning' | 'error'

const levelTone: Record<UrgentLevel, V2Tone> = {
  info: 'brand',
  warning: 'warn',
  error: 'err',
}

const levelIcon: Record<UrgentLevel, typeof Info> = {
  info: Info,
  warning: AlertTriangle,
  error: XOctagon,
}

/**
 * Bandeau de communication urgente - DESIGN §7/§8, RV-31.
 * 3 niveaux (info/warning/error). `role="alert"` pour warning/error, `status` pour info.
 * Pulse de l'icône en error (neutralisé par `prefers-reduced-motion`).
 * Fermeture mémorisée en `localStorage` (par `id`). Libellés fournis i18n par l'appelant.
 */
export function UrgentBanner({
  id,
  level = 'info',
  title,
  message,
  dismissLabel,
  dismissible = true,
  className,
}: {
  /** Identifiant stable du message (clé de fermeture localStorage). */
  id: string
  level?: UrgentLevel
  title?: ReactNode
  message: ReactNode
  /** Libellé accessible du bouton de fermeture (i18n). */
  dismissLabel?: string
  dismissible?: boolean
  className?: string
}) {
  const [dismissed, setDismissed] = useState(() => readUrgentMessageDismissed(id))
  const Icon = levelIcon[level]

  if (dismissed) {
    return null
  }

  const onDismiss = () => {
    setDismissed(true)
    writeUrgentMessageDismissed(id)
  }

  return (
    <div
      role={level === 'info' ? 'status' : 'alert'}
      className={cn('flex items-start gap-3 border-b px-4 py-3', toneSoftClass[levelTone[level]], className)}
    >
      <Icon className={cn('mt-0.5 size-5 shrink-0', level === 'error' && 'animate-pulse')} aria-hidden />
      <div className="min-w-0 flex-1 text-sm">
        {title ? <p className="font-semibold">{title}</p> : null}
        <div className={cn(title && 'mt-0.5')}>{message}</div>
      </div>
      {dismissible ? (
        <button
          type="button"
          onClick={onDismiss}
          aria-label={dismissLabel}
          className="-mr-1 inline-flex size-6 shrink-0 items-center justify-center rounded-pill hover:bg-[var(--v2-ink)]/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <X className="size-4" aria-hidden />
        </button>
      ) : null}
    </div>
  )
}
